import React, { useEffect, useState } from 'react';
import pluralize from 'pluralize';

import commaizeInt from 'js/utils/commaize';
import { ResearchInterest } from 'js/liswp/types';

type Props = {
  interest: ResearchInterest;
  fetchResearchInterestFollowerCount: (interest: { id: number }) => void;
  followResearchInterest: (
    interest: { id: number },
    callback: () => void,
  ) => void;
  unfollowResearchInterest: (
    interest: { id: number },
    callback: () => void,
  ) => void;
  addClickTracking: (url: string) => string;
  loggedIn: boolean;
};

/* A Research Interest Popover, displays the name and follower count of an RI,
 * and allows a user to follow/unfollow the RI.
 * Follower count is loaded async on mount.
 */
const ResearchInterestPopover: React.FC<Props> = ({
  loggedIn = true,
  interest,
  addClickTracking = (url) => url,
  fetchResearchInterestFollowerCount,
  followResearchInterest,
  unfollowResearchInterest,
}) => {
  const [fetchingFollow, setFetchingFollow] = useState(false);

  useEffect(() => {
    if (interest.followerCount == null) {
      fetchResearchInterestFollowerCount(interest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFollowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!fetchingFollow) {
      setFetchingFollow(true);
      if (interest.isFollowing) {
        unfollowResearchInterest(interest, () => setFetchingFollow(false));
      } else {
        followResearchInterest(interest, () => setFetchingFollow(false));
      }
    }
  };

  return (
    <div className="work-card--ri-popover">
      <div className="work-card--popover-outer-header u-mb0x">
        <div className="work-card--popover-expand-wrapper">
          <a
            href={addClickTracking(interest.url)}
            className="work-card--link-underline"
          >
            <div className="work-card--popover-name">{interest.name}</div>
          </a>
          {interest.followerCount == null ? (
            <i className="fa fa-spinner fa-spin u-tcGrayDark u-fs16 u-mt1x" />
          ) : (
            <div>
              {commaizeInt(interest.followerCount)}{' '}
              {pluralize('Follower', interest.followerCount)}
            </div>
          )}
        </div>
        {loggedIn ? (
          <div
            className="work-card--ri-popover-follow-button-wrapper"
            onClick={onFollowClick}
          >
            {interest.isFollowing ? (
              <div className="work-card--ri-popover-button-icon unfollow">
                <i className="fa fa-check" />
              </div>
            ) : (
              <div className="work-card--ri-popover-button-icon follow">
                <i className="fa fa-plus" />
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ResearchInterestPopover;
